import React from "react";
import { AlertCircle } from "lucide-react";

/**
 * Shared 30-min slot grid used by:
 *   - BookingPage (customer booking flow)
 *   - DashboardPage → Block Calendar section (admin)
 *
 * Props:
 *   slots           array of { time, available, available_bays, blocked?, block_reason? }
 *   selectedTime    string | null            highlighted slot
 *   loading         bool                     shows spinner
 *   closedReason    string | null            shows an alert instead of the grid
 *   emptyMessage    string                   shown if slots is [] and no date picked
 *   onSlotClick     (slot) => void           fires for both available and blocked slots
 *                                            (parent decides what to do — customer flow
 *                                             gates on slot.available; admin flow uses
 *                                             blocked/available state to toggle a block)
 *   mode            "book" | "admin"         changes visual treatment for blocked slots
 *   testIdPrefix    string                   for data-testid, default "time-slot"
 */
export function SlotGrid({
  slots,
  selectedTime = null,
  loading = false,
  closedReason = null,
  emptyMessage = "No slots available for this date",
  onSlotClick,
  mode = "book",
  testIdPrefix = "time-slot",
}) {
  if (loading) {
    return (
      <div className="glass rounded-xl p-8 text-center">
        <div className="animate-pulse text-saturn-orange">Loading slots...</div>
      </div>
    );
  }

  if (closedReason) {
    return (
      <div className="glass rounded-xl p-8 text-center">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-red-400 font-rajdhani">{closedReason}</p>
      </div>
    );
  }

  if (!slots || slots.length === 0) {
    return (
      <div className="glass rounded-xl p-8 text-center text-slate-400">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto pr-2"
      data-testid="slot-grid"
    >
      {slots.map((slot) => {
        const isSelected = selectedTime === slot.time;
        const isBlocked = !!slot.blocked;
        const isAvailable = !!slot.available;

        // Determine visual state
        let visual = "glass hover:border-saturn-orange/50 text-white";
        if (isSelected) {
          visual = "bg-saturn-orange text-white";
        } else if (isBlocked) {
          // Admin block — distinct amber styling so it reads as intentional
          visual = mode === "admin"
            ? "bg-amber-900/40 text-amber-300 border border-amber-500/40 hover:bg-amber-800/50"
            : "bg-amber-900/30 text-amber-400 cursor-not-allowed border border-amber-500/30";
        } else if (!isAvailable) {
          visual = "bg-red-900/30 text-red-400 cursor-not-allowed border border-red-500/30";
        }

        // Admin mode: everything is clickable (to toggle blocks / see reason).
        // Customer mode: only available slots respond.
        const clickable = mode === "admin" || isAvailable;

        return (
          <button
            key={slot.time}
            onClick={() => clickable && onSlotClick && onSlotClick(slot)}
            disabled={!clickable}
            className={`p-3 rounded-lg text-center transition-all font-rajdhani ${visual}`}
            data-testid={`${testIdPrefix}-${slot.time}`}
            title={slot.block_reason || ""}
          >
            {slot.time}
            {isBlocked && (
              <span className="block text-xs truncate">
                {mode === "admin" ? (slot.block_reason || "Blocked") : "Not available"}
              </span>
            )}
            {!isBlocked && !isAvailable && (
              <span className="block text-xs">
                {mode === "book" ? "Not available" : "Booked"}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default SlotGrid;
